import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SheetCard } from '../components/SheetCard';
import { CreateSheetModal } from '../components/CreateSheetModal';

interface CharacterSheet {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  bonuses: Array<{ attribute: string; value: number }>;
  maluses: Array<{ attribute: string; value: number }>;
  moves: Array<{ name: string; description: string }>;
  alignment?: string;
  bonds?: string[];
  notes?: string;
  damage?: number;
  armor?: number;
  maxHP?: number;
  level?: number;
  createdAt: string;
}

function CharacterSheetLibrary() {
  const navigate = useNavigate();
  const [sheets, setSheets] = useState<CharacterSheet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingSheet, setEditingSheet] = useState<CharacterSheet | null>(null);

  // Fetch all character sheets
  const fetchSheets = async () => {
    try {
      const response = await fetch('/api/sheets');
      const data = await response.json();

      if (data.success) {
        console.log('[CharacterSheetLibrary] Fetched sheets:', data.sheets);
        setSheets(data.sheets);
      }
    } catch (error) {
      console.error('Error fetching sheets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSheets();
  }, []);

  const handleCreateSheet = async (sheetData: Omit<CharacterSheet, 'id' | 'createdAt'>) => {
    try {
      const response = await fetch('/api/sheets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sheetData),
      });

      const data = await response.json();

      if (data.success) {
        await fetchSheets();
        setIsCreateModalOpen(false);
      }
    } catch (error) {
      console.error('Error creating sheet:', error);
      alert('Failed to create character sheet');
    }
  };

  const handleUpdateSheet = async (sheetData: Omit<CharacterSheet, 'id' | 'createdAt'>) => {
    if (!editingSheet) return;

    console.log('[CharacterSheetLibrary] handleUpdateSheet called with:', sheetData);

    try {
      const response = await fetch(`/api/sheets/${editingSheet.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sheetData),
      });

      const data = await response.json();

      if (data.success) {
        await fetchSheets();
        setEditingSheet(null);
      }
    } catch (error) {
      console.error('Error updating sheet:', error);
      alert('Failed to update character sheet');
    }
  };

  const handleDeleteSheet = async (sheetId: string) => {
    if (!confirm('Are you sure you want to delete this character sheet?')) {
      return;
    }

    try {
      const response = await fetch(`/api/sheets/${sheetId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        await fetchSheets();
      } else {
        alert(data.error || 'Failed to delete character sheet');
      }
    } catch (error) {
      console.error('Error deleting sheet:', error);
      alert('Failed to delete character sheet');
    }
  };

  return (
    <div className="container mx-auto p-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/admin')}
        className="text-purple-400 hover:text-purple-300 mb-4 flex items-center gap-2"
      >
        ← Back to Dashboard
      </button>

      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-purple-400 mb-2">
            Character Sheet Library
          </h1>
          <p className="text-gray-400">
            Manage reusable character sheet templates
          </p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg transition"
        >
          + New Character Sheet
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-600 bg-opacity-20 border border-blue-500 rounded-lg p-4 mb-6">
        <p className="text-blue-300 text-sm">
          ℹ️ Character sheets are <strong>templates</strong> that can be assigned to teams during show setup.
          Sheets include bonuses/maluses and special moves, but not HP or absolute stat values.
        </p>
      </div>

      {/* Sheets Grid */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          <p className="text-gray-400 mt-4">Loading character sheets...</p>
        </div>
      ) : sheets.length === 0 ? (
        <div className="text-center py-12 bg-gray-800 rounded-lg">
          <p className="text-gray-400 text-lg mb-2">No character sheets yet</p>
          <p className="text-gray-500 text-sm mb-4">
            Create your first character sheet template to get started
          </p>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="text-purple-400 hover:text-purple-300 underline"
          >
            Create Character Sheet
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sheets.map((sheet) => (
            <SheetCard
              key={sheet.id}
              sheet={sheet}
              onEdit={() => setEditingSheet(sheet)}
              onDelete={handleDeleteSheet}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(isCreateModalOpen || editingSheet) && (
        <CreateSheetModal
          sheet={editingSheet || undefined}
          onClose={() => {
            setIsCreateModalOpen(false);
            setEditingSheet(null);
          }}
          onSave={editingSheet ? handleUpdateSheet : handleCreateSheet}
        />
      )}
    </div>
  );
}

export default CharacterSheetLibrary;
